// src/components/BookingModal.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { 
  collection, query, getDocs, where, doc, getDoc, Timestamp, deleteDoc, addDoc, runTransaction, limit 
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { X, CheckCircle } from 'lucide-react';
import { subMinutes } from 'date-fns';
import { createNotification } from '../../utils/notifications';

interface Booking {
  id: string;
  horseId: string;
  userId: string;
  userDisplayName: string;
  status: 'confirmed' | 'pending' | 'waitlist' | 'pending-confirmation';
  startTime: Date;
  endTime: Date;
  horseName?: string;
  createdAt?: Date;
  confirmationDeadline?: Date;
}

interface WaitlistEntry {
  id: string;
  facilityId: string;
  horseId: string;
  userId: string;
  startTime: Date;
  endTime: Date;
  createdAt: Date;
  status: string;
  userDisplayName: string;
  horseName?: string;
}

interface Horse {
  id: string;
  name: string;
  breed?: string;
  owner: {
    id: string;
    email: string;
  };
  coRiders?: { [userId: string]: { status: string } };
  booking: Booking | null;
  waitlist: WaitlistEntry[];
}

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBook: (horseId: string) => Promise<void>;
  onConfirmBooking: (booking: Booking) => Promise<void>;
  onCancelBooking: (booking: Booking) => Promise<void>;
  onJoinWaitlist?: (horseId: string) => Promise<void>;
  selectedSlot: { start: Date; end: Date } | null;
  isManager: boolean;
  facility: any;
  canCancelBooking: (booking: Booking) => boolean;
  existingBookings: Booking[];
  waitlistEntries: WaitlistEntry[];
}

export const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
  onBook,
  onConfirmBooking,
  onCancelBooking,
  onJoinWaitlist,
  selectedSlot,
  isManager,
  facility,
  canCancelBooking,
  existingBookings,
  waitlistEntries,
}) => {
  const { user } = useAuth();
  const [horses, setHorses] = useState<Horse[]>([]);
  const [filteredHorses, setFilteredHorses] = useState<Horse[]>([]);
  const [selectedHorse, setSelectedHorse] = useState<Horse | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 640);
  const [isSlotFull, setIsSlotFull] = useState<boolean>(false);
  const [cancelPermissions, setCancelPermissions] = useState<{ [key: string]: boolean }>({});
  const [hasWaitlistEntry, setHasWaitlistEntry] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);

  // Hilfsfunktion zum Vergleichen von Slots bis zur Minute
  const isSameSlot = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate() &&
      date1.getHours() === date2.getHours() &&
      date1.getMinutes() === date2.getMinutes()
    );
  };

  // Überwache die Fenstergröße für responsives Design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Verhindere das Scrollen des Hauptdokuments, wenn das Modal geöffnet ist
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Lade alle Daten, wenn das Modal geöffnet wird
  useEffect(() => {
    if (isOpen) {
      setSelectedHorse(null);
      setSearchTerm('');
      setError(null);
      setSuccess(null);
      loadHorses();
      checkCapacity();
      computeCancelPermissions();
      setHasWaitlistEntry(waitlistEntries.length >= 1);
    }
  }, [isOpen, selectedSlot, facility, waitlistEntries, existingBookings]);

  // Lade alle Pferde aus Firestore
  const loadHorses = useCallback(async () => {
    setLoading(true);
    try {
      let horsesData: any[] = [];

      if (isManager) {
        const horsesSnap = await getDocs(collection(db, 'horses'));
        horsesData = horsesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } else if (user) {
        const ownerQuery = query(
          collection(db, 'horses'),
          where('owner.id', '==', user.uid)
        );

        const coRiderStatusField = `coRiders.${user.uid}.status`;
        const coRiderApprovedQuery = query(
          collection(db, 'horses'),
          where(coRiderStatusField, '==', 'approved')
        );

        const [ownerSnap, coRiderSnap] = await Promise.all([
          getDocs(ownerQuery),
          getDocs(coRiderApprovedQuery)
        ]);

        const ownerHorses = ownerSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const coRiderHorses = coRiderSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const combinedHorses = [...ownerHorses];
        coRiderHorses.forEach(horse => {
          if (!combinedHorses.find(h => h.id === horse.id)) {
            combinedHorses.push(horse);
          }
        });

        horsesData = combinedHorses;
      }

      const horsesList: Horse[] = horsesData.map(h => ({
        ...h,
        booking: null,
        waitlist: []
      }));

      existingBookings.forEach(booking => {
        const horse = horsesList.find(h => h.id === booking.horseId);
        if (horse) {
          horse.booking = booking;
        }
      });

      waitlistEntries.forEach(entry => {
        const horse = horsesList.find(h => h.id === entry.horseId);
        if (horse) {
          horse.waitlist.push(entry);
        }
      });

      setHorses(horsesList);
      setFilteredHorses(horsesList);

      console.log('Loaded Horses with Bookings and Waitlists:', horsesList);
    } catch (err: any) {
      console.error("Fehler beim Laden der Pferde:", err);
      setError(err.message || 'Pferde konnten nicht geladen werden');
    }
    setLoading(false);
  }, [isManager, user, existingBookings, waitlistEntries]);

  // Überprüfe die Kapazität des Slots
  const checkCapacity = useCallback(async () => {
    if (!selectedSlot || !facility?.id) return;

    try {
      const facilityDoc = await getDoc(doc(db, 'facilities', facility.id));
      if (!facilityDoc.exists()) {
        setIsSlotFull(false);
        return;
      }

      const facilityData = facilityDoc.data();
      const capacity = facilityData.capacity || 1;

      const overlappingBookings = existingBookings.filter(booking => {
        return (
          isSameSlot(booking.startTime, selectedSlot.start) &&
          (booking.status === 'confirmed' || booking.status === 'pending-confirmation')
        );
      });

      setIsSlotFull(overlappingBookings.length >= capacity);
    } catch (err: any) {
      console.error("Fehler beim Überprüfen der Kapazität:", err);
      setError(err.message || 'Kapazität konnte nicht überprüft werden');
    }
  }, [selectedSlot, facility, existingBookings, isSameSlot]);

  // Berechne die Berechtigungen zum Stornieren
  const computeCancelPermissions = useCallback(async () => {
    const permissions: { [key: string]: boolean } = {};
    for (const horse of horses) {
      if (horse.booking) {
        permissions[horse.id] = canCancelBooking(horse.booking);
      } else {
        permissions[horse.id] = false;
      }
    }
    setCancelPermissions(permissions);
  }, [horses, canCancelBooking]);

  // Handle für das Stornieren einer Buchung
  const handleCancelBookingInternal = useCallback(async (booking: Booking) => {
    try {
      await onCancelBooking(booking);

      await checkCapacity();
      await computeCancelPermissions();

      if (waitlistEntries.length > 0) {
        const nextInLine = waitlistEntries[0];
        const minBookingNotice = facility.bookingSettings.minBookingNotice;

        const confirmationDeadline = subMinutes(selectedSlot!.start, minBookingNotice);
        console.log(`MinBookingNotice: ${minBookingNotice} Minuten`);
        console.log(`ConfirmationDeadline: ${confirmationDeadline}`);

        const now = new Date();
        console.log(`Now: ${now}`);
        console.log(`ConfirmationDeadline: ${confirmationDeadline}`);

        if (now > confirmationDeadline) {
          setError('Die Frist zur Bestätigung ist bereits abgelaufen.');
          return;
        }

        const newBookingData = {
          facilityId: facility.id,
          horseId: nextInLine.horseId,
          userId: nextInLine.userId,
          startTime: Timestamp.fromDate(nextInLine.startTime),
          endTime: Timestamp.fromDate(nextInLine.endTime),
          createdAt: Timestamp.fromDate(new Date()),
          status: 'pending-confirmation',
          confirmationDeadline: Timestamp.fromDate(confirmationDeadline)
        };

        const bookingRef = await addDoc(collection(db, 'bookings'), newBookingData);
        console.log('Buchung als pending-confirmation erstellt:', newBookingData);

        await deleteDoc(doc(db, 'waitlist', nextInLine.id));

        await createNotification({
          recipientId: nextInLine.userId,
          priority: 'high',
          metadata: {
            type: 'BOOKING_PENDING_CONFIRMATION',
            bookingId: bookingRef.id,
            facilityName: facility.name,
            horseName: nextInLine.horseName || 'Unbekanntes Pferd',
            startTime: nextInLine.startTime
          }
        });

        setSuccess('Buchung erfolgreich storniert und Wartelistenplatz übernommen.');
      } else {
        setSuccess('Buchung erfolgreich storniert.');
      }

    } catch (error: any) {
      console.error('Error canceling booking:', error);
      setError(error.message || 'Buchung konnte nicht storniert werden.');
    }
  }, [onCancelBooking, checkCapacity, computeCancelPermissions, waitlistEntries, facility, selectedSlot]);

  // Handle für das Hinzufügen zur Warteliste
  const handleJoinWaitlistInternal = useCallback(async (horseId: string) => {
    if (!selectedSlot || !facility || !user) return;

    const existingWaitlist = waitlistEntries.find(entry => 
      entry.horseId === horseId && 
      isSameSlot(entry.startTime, selectedSlot.start)
    );

    if (existingWaitlist) {
      setError('Es existiert bereits ein Wartelistenplatz für dieses Pferd in diesem Slot.');
      return;
    }

    const bookingsForSlot = existingBookings.filter(booking => 
      isSameSlot(booking.startTime, selectedSlot.start)
    );

    if (bookingsForSlot.length < facility.capacity) {
      setError('Dieser Slot ist noch verfügbar. Bitte buchen Sie stattdessen.');
      return;
    }

    try {
      await runTransaction(db, async (transaction) => {
        const waitlistRef = collection(db, 'waitlist');
        const q = query(
          waitlistRef,
          where('facilityId', '==', facility.id),
          where('horseId', '==', horseId),
          where('startTime', '==', Timestamp.fromDate(selectedSlot.start)),
          limit(1)
        );

        const waitlistSnap = await getDocs(q);

        if (!waitlistSnap.empty) {
          throw new Error('Es existiert bereits ein Wartelistenplatz für dieses Pferd in diesem Slot.');
        }

        const newWaitlistEntry = {
          facilityId: facility.id,
          horseId: horseId,
          userId: user.uid,
          startTime: Timestamp.fromDate(selectedSlot.start),
          endTime: Timestamp.fromDate(selectedSlot.end),
          createdAt: Timestamp.now(),
          status: 'pending',
          userDisplayName: user.displayName || 'Unbekannt',
          horseName: existingBookings.find(b => b.horseId === horseId)?.horseName || 'Unbekannt'
        };

        await addDoc(waitlistRef, newWaitlistEntry);
      });

      setSuccess('Sie wurden erfolgreich zur Warteliste hinzugefügt.');
    } catch (error: any) {
      console.error('Error joining waitlist:', error);
      setError(error.message || 'Wartelistenplatz konnte nicht erstellt werden.');
    }
  }, [selectedSlot, facility, user, waitlistEntries, existingBookings, isSameSlot]);

  // Funktion zum Entfernen eines Wartelisten-Eintrags
  const handleCancelWaitlistEntry = useCallback(async (entryId: string) => {
    try {
      await deleteDoc(doc(db, 'waitlist', entryId));
      setSuccess('Wartelistenplatz wurde erfolgreich storniert.');
    } catch (error: any) {
      console.error('Error canceling waitlist entry:', error);
      setError(error.message || 'Wartelistenplatz konnte nicht storniert werden.');
    }
  }, []);

  // Funktion zur Bestimmung, ob ein Wartelisten-Eintrag vom Nutzer storniert werden kann
  const canCancelWaitlistEntry = useCallback((entry: WaitlistEntry) => {
    if (isManager) return true;
    if (entry.userId === user?.uid) return true;

    const horse = horses.find(h => h.id === entry.horseId);
    if (!horse) return false;

    if (horse.owner.id === user?.uid) return true;
    if (horse.coRiders && horse.coRiders[user?.uid]?.status === 'approved') return true;

    return false;
  }, [isManager, user, horses]);

  // Filtere die Pferde basierend auf dem Suchbegriff
  useEffect(() => {
    if (searchTerm) {
      const filtered = horses.filter(horse =>
        horse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        horse.owner.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredHorses(filtered);
    } else {
      setFilteredHorses(horses);
    }
  }, [searchTerm, horses]);

  // Debugging: Überprüfe die übergebenen Buchungen und Wartelistenplätze
  useEffect(() => {
    console.log('BookingModal: existingBookings:', existingBookings);
    console.log('BookingModal: waitlistEntries:', waitlistEntries);
  }, [existingBookings, waitlistEntries]);

  if (!isOpen || !selectedSlot) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-[99999] overflow-hidden">
      <div
        className={`
          min-h-full
          ${isMobile ? 'h-full' : 'py-6'}
          flex items-center justify-center
        `}
      >
        <div
          className={`
            w-full
            ${isMobile ? 'h-full' : 'max-h-[calc(100vh-3rem)]'}
            ${isMobile ? '' : 'max-w-lg'}
            bg-white dark:bg-gray-800
            ${isMobile ? 'rounded-none' : 'rounded-lg shadow-xl'}
            flex flex-col
            relative
          `}
        >
          {/* Header */}
          <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Buchungsübersicht</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Slot-Informationen */}
            <div className="bg-blue-50 dark:bg-blue-900/50 rounded-md p-4">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedSlot.start.toLocaleDateString('de-DE', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-200">
                {selectedSlot.start.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })} Uhr
                <span className="ml-2 text-xs">
                  ({facility.bookingSettings.defaultDuration} Minuten)
                </span>
              </p>
            </div>

            {/* Suchfeld */}
            <div>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Pferd oder Besitzer suchen..."
                />
              </div>

              {/* Pferde-Liste */}
              <div className="mt-4 space-y-2">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="text-gray-500 dark:text-gray-400">Lädt...</div>
                  </div>
                ) : filteredHorses.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    Keine Pferde verfügbar
                  </p>
                ) : (
                  filteredHorses.map(horse => {
                    const booking = horse.booking;
                    let statusText = '';
                    let actionButton = null;
                    let cancelButton = null;

                    if (booking) {
                      // Logik für nachgerückte Wartelistenplätze
                      if (booking.status === 'pending-confirmation') {
                        if (booking.confirmationDeadline && new Date() <= booking.confirmationDeadline) {
                          const deadlineFormatted = booking.confirmationDeadline.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
                          
                          // Prüfen ob es eine andere aktive Buchung für dieses Pferd im gleichen Slot gibt
                          const hasOtherBooking = existingBookings.some(otherBooking => 
                            otherBooking.horseId === horse.id && 
                            otherBooking.id !== booking.id && 
                            isSameSlot(otherBooking.startTime, selectedSlot.start) &&
                            ['confirmed', 'pending'].includes(otherBooking.status)
                          );

                          if (!hasOtherBooking && user?.uid === booking.userId) {
                            statusText = `(Nachrückend, bitte bis ${deadlineFormatted} übernehmen)`;
                            actionButton = (
                              <button
                                onClick={() => onConfirmBooking(booking)}
                                className="px-3 py-1 text-sm font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900 rounded"
                              >
                                Übernehmen
                              </button>
                            );
                          } else if (hasOtherBooking) {
                            statusText = '(Wartelistenplatz noch nicht nachgerückt)';
                          } else {
                            statusText = '(Wartet auf Bestätigung durch den Wartelistenplatz-Inhaber)';
                          }
                        } else {
                          statusText = '(Frist abgelaufen)';
                        }
                      } else if (booking.status === 'pending' && isManager) {
                        actionButton = (
                          <button
                            onClick={() => onConfirmBooking(booking)}
                            className="mr-2 p-1.5 rounded-full text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50"
                            title="Buchung bestätigen"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        );
                      } else if (booking.status === 'confirmed') {
                        statusText = `(Gebucht von ${booking.userDisplayName})`;
                      }

                      // Stornieren-Button für berechtigte Benutzer
                      if (canCancelBooking(booking)) {
                        cancelButton = (
                          <button
                            onClick={() => handleCancelBookingInternal(booking)}
                            className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 rounded-md transition-colors"
                            title="Buchung stornieren"
                          >
                            Stornieren
                          </button>
                        );
                      }
                    }

                    return (
                      <div
                        key={horse.id}
                        className={`w-full px-4 py-3 rounded-md transition-colors ${
                          selectedHorse?.id === horse.id
                            ? 'bg-indigo-50 dark:bg-indigo-900/50 border-2 border-indigo-500 dark:border-indigo-400'
                            : 'border border-gray-300 dark:border-gray-600'
                        } ${horse.booking ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {horse.name}
                              </span>
                              {statusText && (
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                  {statusText}
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {horse.owner.email}
                            </span>
                          </div>
                          <div className="ml-4 flex items-center space-x-2">
                            {!horse.booking && (
                              <button
                                onClick={() => setSelectedHorse(horse)}
                                className="px-3 py-1 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900 rounded"
                              >
                                Auswählen
                              </button>
                            )}
                            {actionButton}
                            {cancelButton}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Warteliste */}
              {waitlistEntries.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-md font-medium text-yellow-700 dark:text-yellow-300">Warteliste</h4>
                  <ul className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {waitlistEntries.map(entry => {
                      const horse = horses.find(h => h.id === entry.horseId);
                      const canCancel = canCancelWaitlistEntry(entry);

                      return (
                        <li key={entry.id} className="flex justify-between items-center p-3 bg-yellow-100 dark:bg-yellow-800 rounded-md">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {entry.userDisplayName}
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              Pferd: {entry.horseName}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              Zeit: {entry.startTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                            </span>
                          </div>
                          {canCancel && (
                            <button
                              onClick={() => handleCancelWaitlistEntry(entry.id)}
                              className="text-red-500 dark:text-red-300 hover:text-red-600 dark:hover:text-red-400 text-xs"
                              title="Wartelistenplatz stornieren"
                            >
                              Stornieren
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Erfolgsanzeige */}
              {success && (
                <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/50 rounded-md">
                  <p className="text-sm text-green-700 dark:text-green-200">{success}</p>
                </div>
              )}

              {/* Fehleranzeige */}
              {error && (
                <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/50 rounded-md">
                  <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 p-4">
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Abbrechen
              </button>
              {isSlotFull ? (
                <button
                  onClick={() => {
                    if (selectedHorse && onJoinWaitlist) {
                      handleJoinWaitlistInternal(selectedHorse.id);
                    }
                  }}
                  disabled={!selectedHorse || hasWaitlistEntry || (selectedHorse?.booking?.status === 'pending-confirmation')}
                  className={`w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                    ${selectedHorse && !hasWaitlistEntry && (selectedHorse?.booking?.status !== 'pending-confirmation')
                      ? 'bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-500 dark:hover:bg-yellow-600'
                      : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                    }`}
                >
                  Auf Warteliste setzen
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (selectedHorse) {
                      onBook(selectedHorse.id);
                    }
                  }}
                  disabled={!selectedHorse}
                  className={`w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                    ${selectedHorse
                      ? 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'
                      : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                    }`}
                >
                  Buchung bestätigen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
